/* ══════════════════════════════════════════════════════════════════════════
 * WHERE THE BRAND CARD GOES, AND THE THREE NUMBERS THAT DECIDE IT
 *
 * Al, 14 September 2026:
 * > *"Between every 10th restaurant on happy hour, daily deals, or game day,
 * >  we should have a card that is specific and only specific to that campaign
 * >  for the brands… They can put a video in there… a quick 10, 15 second
 * >  commercial… photos… what's coming, what's happening."*
 *
 * ⚠️⚠️ THE NUMBERS ARE NOT IN THIS FILE AND MUST NEVER BE. `brand_card
 * .every_nth`, `brand_card.min_row_length` and `brand_card.max_per_row` are
 * rows in `platform_settings`, seeded by migration 075, changed by the Super
 * Admin. Migration 054 §5 and 075 §2 both say it in the same words: never a
 * constant in the React build, never a default in a component. This module
 * receives them from `/discovery/search` and refuses to draw anything when it
 * has not been told.
 *
 * ⚠️ "AT MOST ONE, WHERE THE ROW IS LONG ENOUGH" — NOT "ALWAYS AT POSITION
 * TEN". Every tenth card in a row of four is no card at all, and a rule that
 * silently does nothing is worse than no rule. That is what `min_row_length`
 * is for, and why a row shorter than the interval puts the card at its end
 * rather than dropping it. `Eleater Prototype/eleat_campaign.js` says the same
 * in the same shape; this is that rule in React.
 *
 * ⚠️ AND A BRAND MONTH NEVER REACHES LAST-MINUTE SPECIALS. Surplus food going
 * out at the end of service is not a brand's campaign. Guarded in the database
 * by the trigger in migration 075, in `placement.service.js`, in
 * `eleat_campaign.js`, and here. A rule guarded in one place is a rule with
 * one unguarded door.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE ONE RAIL, NAMED ONCE. It is the schema's own `deal_type` value
   (migration 001, never altered), not a screen word. */
export const NEVER_RAIL = 'last_minute';

/**
 * May this rail carry a brand month at all?
 *
 * ⚠️ TWO REFUSALS, AND THEY ARE DIFFERENT. `NEVER_RAIL` is Al's rule and does
 * not bend. `rails` is the Super Admin's list, which may legitimately be
 * shorter — a city running a month on Game Day only. Both have to pass.
 */
export function railMayCarry(placement, railType) {
  if (!placement) return false;
  if (railType === NEVER_RAIL) return false;
  if (placement.may_carry_a_month === false) return false;
  const rails = placement.rails;
  if (Array.isArray(rails) && !rails.includes(railType)) return false;
  return true;
}

/**
 * Should this venue's card wear the sponsored band?
 *
 * ⚠️ THE BAND IS DISCLOSURE, AND IT IS DRAWN FROM THE SERVER'S ANSWER ONLY.
 * `v.sponsor` arrives from `/discovery/search` and is null on every rail a
 * month does not reach — but the rail is checked here as well, because a card
 * is drawn by five screens and only one of them is Home.
 */
export function bandFor(v, placement, railType) {
  if (!v || !v.sponsor || !v.sponsor.brand) return null;
  if (!railMayCarry(placement, railType)) return null;
  return v.sponsor;
}

/**
 * Where the brand card sits among the venue cards.
 *
 * @returns {number[]} indexes to insert BEFORE, ascending. Empty when the row
 *          does not earn one — which is the ordinary answer and not a fault.
 *
 * ⚠️ COUNTED IN CARDS, NEVER IN SIBLINGS. A rail also carries a "View all"
 * end-card, so counting children would drop the brand card in a different
 * place on different rows. `eleat_campaign.js` learned this the hard way and
 * says so.
 */
export function slotsFor(cardCount, placement) {
  if (!placement || !placement.brand_card) return [];
  const every = Number(placement.every_nth);
  const min = Number(placement.min_row_length);
  const max = Number(placement.max_per_row);
  /* ⚠️ NOT TOLD, NOT DRAWN. A missing setting is not an invitation to pick a
     number — it is the one case where doing nothing is the honest answer. */
  if (!Number.isFinite(every) || every < 1) return [];
  if (!Number.isFinite(min) || !Number.isFinite(max) || max < 1) return [];
  if (cardCount < min) return [];

  const out = [];
  /* ⚠️ A ROW SHORTER THAN THE INTERVAL STILL GETS ITS ONE CARD, AT THE END —
     `eleat_campaign.js`'s own `Math.min(every, cards.length)`. A row of eight with an
     interval of ten is long enough to be worth a brand's money; refusing it
     would make the setting silently do nothing on most real rails. */
  for (let at = Math.min(every, cardCount); at <= cardCount && out.length < max; at += every) {
    out.push(at);
  }
  return out;
}

/**
 * Where the brand card leads.
 *
 * ⚠️ TO THE CAMPAIGN'S OWN RAIL, NEVER TO A VENUE. The card is the brand's,
 * not a restaurant's, and sending somebody from an advertisement into one
 * particular bar would be selling rank — which the locked model forbids
 * outright: *"You buy entry to the carousel, never rank within it."*
 */
export function brandHref(railType) {
  return `?all=${encodeURIComponent(railType)}`;
}

/**
 * Where the sponsored band leads.
 *
 * ⚠️⚠️ GAME DAY, NOT ABOUT — AND THAT IS AL'S OWN WORDS, 15 September 2026:
 * *"if a restaurant has a purple sponsorship and they press it, they should go
 * directly to Game Day, not the about page of the restaurant."*
 * Quoted in `10_Home_LOCKED.html` beside `.spon-band{cursor:pointer}`, which
 * is how the locked screen records that the band is a CONTROL and not a label.
 *
 * ⚠️ `gd` IS THE SCREEN'S TAB KEY, not the deal type. `tabs.js` maps the four:
 * hh · daily · lm · gd. The enum values are happy_hour · daily · last_minute ·
 * game_day and the two vocabularies are never mixed.
 */
export const BAND_TAB = 'gd';

export function bandHref(venueId) {
  return `?venue=${encodeURIComponent(venueId)}&tab=${BAND_TAB}`;
}
